// Forecast-cancel command (EPIC #157, phase P5.7 — design §2.2).
//
// `onCancelForecast` moved out of `AppInner` into a `(deps, args)` command. Soft cancel
// only: flip the lifecycle row server-side (`POST /api/forecasts/:trace/cancel`) + abort the
// client's in-flight execution + clear the reload-resume handle. Chatboard's server-side
// cancel handler fans the POST out to every configured FORECAST_BACKEND_URL_<arm>, so the
// forecaster's agent loop is actually aborted (no wasted tokens after the click).
//
// Behavior-preserving. The migration-mandated seams:
//   • `runStateRef.current` / the `cancellableRun` useMemo → recomputed from
//     `deps.store.getState()` via the shared `selectCancellableRun` selector (the same one
//     App's `canCancel` render gate calls, so both pick the same running run; the traceId
//     fallbacks below make the command's cancelability a superset of that gate)
//   • `execByRunIdRef.current` → the `execTable` module singleton (§7 #3)
//   • `dispatchRun(…)` → `deps.store.dispatch(…)`
//   • the root abort ref + the three UI setters are threaded via the deps bags; `activeTrace`
//     (the in-tab trace handle — React state, not yet in the store) is passed as an arg.
// The `/cancel` POST goes through `deps.services.api` (P5.11 drained the raw fetch).
import { captureChatTarget } from "../lib/forecast/chatTarget.js";
import { activeSet, runCancelled } from "../lib/forecast/reducer.js";
import { selectCancellableRun } from "../lib/forecast/selectors.js";
import { PENDING_FORECAST_KEY } from "../lib/forecast/storageKeys.js";
import { execTable } from "../store/execTable.js";
import { bindIntent } from "../lib/telemetry/intentDeps.js";
import { beginIntent, type BeginIntentOptions } from "../lib/telemetry/instrument.js";
import type { CommandDeps } from "./deps.js";

// ── deps typing ───────────────────────────────────────────────────────────────
// Pragmatic types at the React boundary (decision C): the store carries real domain types;
// the UI setters / root abort ref are React handles this command doesn't own.
type AnyFn = (...args: any[]) => any;
type Ref<T = any> = { current: T };

/** React setters cancel writes (ephemeral UI state; moves down in P7). */
interface CancelUi {
  setForecastingPhase: AnyFn;
  setLoading: AnyFn;
  setActiveTrace: AnyFn;
}

/** Stable imperative handles: the root run's AbortController ref (§7 #2). */
interface CancelRefs {
  forecastAbortRef: Ref;
}

/** Full deps for the cancel command: shared base + the React-bound bags. */
export interface CancelDeps extends CommandDeps {
  ui: CancelUi;
  refs: CancelRefs;
}

/** Args: the in-tab trace handle (React state, not in the store) used as a traceId fallback. */
export interface CancelArgs {
  activeTrace?: { traceId?: string | null } | null;
  /** The control that asked (telemetry `origin`); defaults to the chat's cancel button. */
  origin?: BeginIntentOptions["origin"];
}

/**
 * Cancel the live run in the active forecast's tree — the ROOT itself OR a running BRANCH.
 * After a CLEAR detaches a still-running forecast, the user re-opens it (or a forecast with a
 * running branch) from the log and stops it here. Synchronous: the `/cancel` POST is
 * fire-and-forget (the UI is already in the cancelled state).
 */
export function cancelForecast(rawDeps: CancelDeps, args: CancelArgs = {}): void {
  // Recompute the cancel target from the store via the SAME selector App's `canCancel`
  // gate uses, so both pick the same running run. (The traceId fallbacks below add the
  // active run / in-tab handle for a reattached root not in the tree scan, so cancel can
  // still fire in a few cases the bare selector wouldn't — the selector is the shared
  // target-selection, not the whole cancelability contract.)
  // Capture the cancel target once, first, through the sanctioned capture (C-10); nothing below
  // asks the store again which run is on screen. A selection naming no loaded run captures `null`.
  const s = rawDeps.store.getState();
  const activeRunId = captureChatTarget(s).runId;
  const running = selectCancellableRun(s, activeRunId);
  const activeRun = activeRunId ? s.runsById[activeRunId] : null;
  // Prefer the live run's traceId; fall back to the active run / in-tab handle (a
  // reattached/reloaded root whose run row didn't surface in the tree scan).
  const traceId = running?.traceId || activeRun?.traceId || args.activeTrace?.traceId || null;
  if (!traceId) return;
  const runId = running?.runId || activeRunId || null;
  // The user's cancel (P2 design §4.6); the run it stops then ends its own intent `cancelled`.
  const intent = beginIntent(
    "forecast.cancel",
    { forecast_id: runId, trace_id: traceId },
    { origin: args.origin ?? "chat.cancel-forecast" },
  );
  const deps = bindIntent(rawDeps, intent);
  // Cancelling the ROOT the user is viewing tears down the visible chat state (mirrors the
  // original single-forecast cancel); cancelling a BRANCH leaves the root active/visible and
  // only stops that branch's execution.
  const cancellingRoot = runId === activeRunId;
  // Optimistic UI: drop the in-tab trace handle + pending-resume key.
  deps.ui.setActiveTrace(null);
  try {
    sessionStorage.removeItem(PENDING_FORECAST_KEY);
  } catch {
    /* best-effort — sessionStorage may be unavailable (private mode / quota) */
  }
  if (cancellingRoot) {
    if (deps.refs.forecastAbortRef.current) {
      deps.refs.forecastAbortRef.current.abort();
      deps.refs.forecastAbortRef.current = null;
    }
    // The cancelled terminal line is run-projected by selectChatProjection
    // (run.status === CANCELLED → "Forecast cancelled"). No persisted entry here — the
    // projection is the single owner so the line doesn't double-render on reload.
    deps.ui.setForecastingPhase(null);
    deps.store.dispatch(activeSet(null)); // dual-write active clear to the store
    deps.ui.setLoading(false);
  } else if (runId) {
    // Branch: abort just that run's client execution via the exec table (branches claim it
    // with their AbortController at createBranch).
    execTable.abort(runId);
  }
  // Flip the run's status to cancelled so the RUNNING badge updates without waiting for a
  // reload. upsertForecast on the server doesn't touch the status column, so the local
  // mutation persisted through the save-effect won't clobber the server-side 'cancelled' flag.
  if (runId) {
    deps.store.dispatch(runCancelled(runId));
  }
  // Fire-and-forget — UI's already in the cancelled state. `api.cancel` never throws
  // (best-effort; reaper catches orphans), so no `.catch` is needed.
  void Promise.resolve(deps.services.api.cancel(traceId)).then(
    (r) => intent.end(r?.ok === false ? "error" : "ok"),
    () => intent.end("error"),
  );
}
